import {createFileRoute} from '@tanstack/react-router';

import {parseNonEmpty, parseTrueFlag} from '@/lib/search-params';
import {AccountMappingPage} from '@/modules/admin-settings/pages/AccountMappingPage';

/**
 * URL state for the account-mapping settings page.
 *
 * Access is gated centrally by the existing `/admin/settings/**` rule in
 * `ROUTE_REQUIREMENTS` (`settings:view`), which is also what the endpoints
 * behind this page require — so no finer rule is needed here. Editing is gated
 * per-control on `settings:edit`, and server-side regardless.
 */
/** Which surface of the page is showing. */
export type AccountMappingTab = 'combinations' | 'staff' | 'defaults';

export interface AccountMappingSearch {
  /** Free-text filter over the combination labels, applied client-side. */
  q?: string;
  /** Show only combinations that would fail to resolve. Shareable, hence a param. */
  gaps?: true;
  /** The open tab. In the URL so a link can point at the resolution tester. */
  tab?: AccountMappingTab;
}

const TABS: ReadonlyArray<AccountMappingTab> = ['combinations', 'staff', 'defaults'];

const validateAccountMappingSearch = (search: Record<string, unknown>): AccountMappingSearch => {
  // `find` already returns the narrowed tab, so nothing here needs a cast. An
  // unrecognised one is dropped rather than substituted, leaving the page to
  // apply its own default (`tab ?? 'combinations'`) — one way of saying it.
  const tab = TABS.find((known) => known === search.tab);
  return {
    q: parseNonEmpty(search.q),
    ...(tab ? {tab} : {}),
    ...(parseTrueFlag(search.gaps) ? {gaps: true as const} : {}),
  };
};

export const Route = createFileRoute('/_authenticated/admin/settings/account-mapping/')({
  validateSearch: validateAccountMappingSearch,
  component: AccountMappingPage,
});
